/**
 * GuardianMatchTable — inline "similar records" list for the guardian
 * identity duplicate check.
 *
 * Ports Flutter `patient_selection_widget.dart` (rendered inline by
 * `identity_child_widget.dart` when `isGuardian: true`). Unlike the patient's
 * OWN identity dedupe — which loads/abandons into the existing patient — the
 * guardian flow LINKS an existing person as the guardian: selecting a row
 * auto-fills the guardian fields from that record and clears the matches
 * (`onGuardianSelected` in the Flutter source). It does NOT navigate away.
 *
 * Flutter parity notes:
 *   - Header copy is verbatim: "{n} SIMILAR RECORD FOUND" (Flutter does not
 *     pluralize — `patient_selection_widget.dart:299`).
 *   - Columns: Name / ID Type / ID Number (same three, same order).
 *   - Two actions: "Use Selected Record" (enabled once a row is selected) and
 *     "Create New Record Anyway" (`showCreateNewButton: true` for guardians).
 *   - The shared Flutter helper string says the new record "will be
 *     discarded" — that is written for the patient-identity path and is
 *     inaccurate for the guardian (which links, not discards), so the copy
 *     here is guardian-specific. Behaviour matches Flutter; only the
 *     misleading shared sentence is corrected.
 *
 * Per CLAUDE.md "Flutter-web workaround simplification": the Flutter widget's
 * row-tap → sliding "View Patient" popup → "Use This Record" two-step is
 * collapsed to in-table single selection + a footer "Use Selected Record"
 * button. Same outcome, fewer surfaces.
 */

import { useState } from 'react';
// GAP (SYSTEM-RULES §3): no Signal DS glyph for "add a person / new record" (`UserPlus`); it stays
// lucide in both looks.
import { UserPlus } from 'lucide-react';
import {
  Button,
  CustomTableList,
  type CustomTableColumn,
} from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import type { MatchingPatient } from '@/types/patient-api';

export interface GuardianMatchTableProps {
  matches: MatchingPatient[];
  /** Resolve an ID-type GUID to its display name (from the master list). */
  idTypeName: (id: string | null) => string;
  /** Link the selected existing person as the guardian (auto-fill + clear). */
  onUseRecord: (match: MatchingPatient) => void;
  /** Keep the entered guardian as a new record — clears the save-gate. */
  onCreateNewAnyway: () => void;
}

export function GuardianMatchTable({
  matches,
  idTypeName,
  onUseRecord,
  onCreateNewAnyway,
}: GuardianMatchTableProps) {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  if (matches.length === 0) return null;

  const displayName = (m: MatchingPatient) =>
    m.name ||
    [m.firstName, m.middleName, m.lastName].filter(Boolean).join(' ') ||
    '—';

  // Identity lives under `identification` on the wire; the top-level
  // idType/idNumber are legacy nulls. Fall back to top-level just in case.
  const idTypeOf = (m: MatchingPatient) => m.identification?.idType ?? m.idType;
  const idNumberOf = (m: MatchingPatient) =>
    m.identification?.idNumber ?? m.idNumber;

  // `CustomTableList.onRowClick` hands back the row object, not its index —
  // `matches` is the same array reference passed as `data`, so `indexOf`
  // recovers the index safely (no duplicate object identities in this list).
  const indexOf = (m: MatchingPatient) => matches.indexOf(m);

  const columns: CustomTableColumn<MatchingPatient>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (m) => {
        const selected = selectedIdx === indexOf(m);
        return (
          <span className="flex items-center gap-2 min-w-0">
            <span
              aria-hidden
              className={cn(
                'inline-block h-4 w-4 rounded-full border',
                selected
                  ? 'border-brand-primary bg-brand-primary'
                  : 'border-border-default',
              )}
            />
            <span className="text-sm font-medium truncate">
              {displayName(m)}
            </span>
          </span>
        );
      },
    },
    {
      id: 'idType',
      header: 'ID Type',
      cell: (m) => (
        <span className="text-sm text-text-muted">
          {idTypeName(idTypeOf(m))}
        </span>
      ),
    },
    {
      id: 'idNumber',
      header: 'ID Number',
      cell: (m) => (
        <span className="text-sm font-mono text-text-muted">
          {idNumberOf(m) ?? '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="rounded-md border border-status-warning bg-status-warning-bg/40 p-3">
      <p className="text-xs font-bold uppercase tracking-wide text-status-amber-text">
        {matches.length} SIMILAR RECORD FOUND
      </p>

      <CustomTableList
        className="mt-2"
        data={matches}
        columns={columns}
        caption={`${matches.length} similar record found`}
        onRowClick={(m) => setSelectedIdx(indexOf(m))}
        isRowSelected={(m) => selectedIdx === indexOf(m)}
      />

      <p className="mt-2 text-sm text-text-muted">
        Select an existing record to use as this patient&rsquo;s guardian, or
        continue with the details you entered.
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={selectedIdx === null}
          onClick={() => {
            if (selectedIdx === null) return;
            onUseRecord(matches[selectedIdx]!);
            setSelectedIdx(null);
          }}
        >
          Use Selected Record
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={onCreateNewAnyway}
        >
          <UserPlus className="size-4 mr-1" />
          Create New Record Anyway
        </Button>
      </div>
    </div>
  );
}
